// ─────────────────────────────────────────────────────────────────────────────
// Shared presentational atoms for the Grievance Log reports.
//
// These match the host app's existing look (.card / .badge / slate-blue palette)
// without introducing a new design system or any charting dependency — all
// "charts" are plain CSS/div bars. Reused across all 9 reports to stay DRY.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import { printReport, downloadReportDoc } from "../reportExport";

export const PALETTE =["#0f4c81", "#0891b2", "#059669", "#7c3aed", "#be123c", "#d97706", "#0369a1", "#475569"];

// Muted "— not in feed" affordance for missing/reserved fields. Never blank,
// never a crash. Use <Missing/> for unknown values and <Dash/> for plain blanks.
export function Missing({ title = "Not carried by the current feed" }) {
  return (
    <span title={title} style={{ color: "#94a3b8", fontStyle: "italic", fontSize: "0.92em" }}>
      — not in feed
    </span>
  );
}

export function Dash() {
  return <span style={{ color: "#94a3b8" }}>—</span>;
}

// Render a value, or the muted missing-state when it is null/undefined/empty.
export function val(v, { missing = false } = {}) {
  if (v === null || v === undefined || v === "") return missing ? <Missing /> : <Dash />;
  return v;
}

// The scope a report was run over, supplied once by MetricsApp so no report has
// to thread it through. Used only by the export — a printed report that does
// not say which facility, which data and who ran it is not evidence of
// anything.
export const ReportExportContext = React.createContext({});

// Standard report header — and the one place export is wired, so every report
// built on ReportShell gets it, including ones that do not exist yet.
export function ReportShell({ title, subtitle, right, children }) {
  const ctx = React.useContext(ReportExportContext);
  const bodyRef = React.useRef(null);
  const [notice, setNotice] = React.useState(null);
  const opts = { title, subtitle, facilityName: ctx.facilityName, generatedBy: ctx.generatedBy, source: ctx.source };
  const doPrint = () => setNotice(printReport(bodyRef.current, opts));
  const btn = { background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 7, padding: "6px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0 }}>{title}</h2>
          {subtitle && <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{subtitle}</div>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {right}
          <button type="button" onClick={doPrint} style={btn}
            title="Opens the print dialog — choose “Save as PDF” there for a PDF">🖨 Print / PDF</button>
          <button type="button" onClick={() => downloadReportDoc(bodyRef.current, opts)} style={btn}
            title={ctx.generatedBy ? `Word document, authored to ${ctx.generatedBy}` : "Word document"}>⬇ Word</button>
        </div>
      </div>
      {/* A blocked pop-up is the one failure here that is otherwise silent. */}
      {notice && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 12.5, display: "flex", gap: 10 }}>
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)}
            style={{ marginLeft: "auto", background: "none", border: "none", color: "#92400e", cursor: "pointer", fontWeight: 700, fontFamily: "inherit" }}>✕</button>
        </div>
      )}
      {/* Everything inside is what exports. The header and the buttons are
          outside it on purpose — the document writes its own header. */}
      <div ref={bodyRef}>{children}</div>
    </div>
  );
}

export function Card({ children, style }) {
  return <div className="card" style={{ padding: 18, ...style }}>{children}</div>;
}

export function SectionTitle({ children, style }) {
  return <div className="section-header" style={style}>{children}</div>;
}

// KPI tile with green/yellow/red tone states (like the Reimbursement card).
const TONES = {
  green:  { color: "#166534", bg: "#f0fdf4", border: "#bbf7d0" },
  yellow: { color: "#92400e", bg: "#fffbeb", border: "#fde68a" },
  red:    { color: "#991b1b", bg: "#fef2f2", border: "#fecaca" },
  neutral:{ color: "#0f4c81", bg: "#eff6ff", border: "#bfdbfe" },
};

export function KPITile({ label, value, sub, tone = "neutral" }) {
  const t = TONES[tone] || TONES.neutral;
  return (
    <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 10, padding: "16px 18px", flex: "1 1 160px", minWidth: 150 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: t.color, textTransform: "uppercase", letterSpacing: ".06em", opacity: 0.85 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color: t.color, lineHeight: 1.1, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: t.color, opacity: 0.8, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// Horizontal CSS bar chart. data: [{ label, value, color?, sub? }]
export function MiniBars({ data, max, valueFmt = (v) => v }) {
  const peak = max != null ? max : Math.max(1, ...(data || []).map((d) => d.value || 0));
  if (!data || !data.length) {
    return <div style={{ color: "#94a3b8", fontSize: 13, padding: "8px 0" }}>No data for this scope.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {data.map((d, i) => (
        <div key={d.label + i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 130, fontSize: 12.5, color: "#475569", textAlign: "right", flexShrink: 0 }}>{d.label}</div>
          <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 6, height: 22, position: "relative", overflow: "hidden" }}>
            <div style={{ width: `${Math.max(2, ((d.value || 0) / peak) * 100)}%`, height: "100%", background: d.color || PALETTE[i % PALETTE.length], borderRadius: 6, transition: "width .3s" }} />
          </div>
          <div style={{ width: 56, fontSize: 13, fontWeight: 700, color: "#0f172a", flexShrink: 0 }}>{valueFmt(d.value)}{d.sub ? <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: 11 }}> {d.sub}</span> : null}</div>
        </div>
      ))}
    </div>
  );
}

// Status / category chips reusing the host .badge look.
export function Pill({ label, color, bg, border }) {
  return (
    <span className="badge" style={{ color, background: bg, border: border ? `1px solid ${border}` : "none" }}>{label}</span>
  );
}

// Notification check/cross (the F585 gate).
export function NotifyMark({ sent }) {
  if (sent === true) return <span title="Resident notified" style={{ color: "#059669", fontWeight: 800 }}>✓</span>;
  if (sent === false) return <span title="Resident NOT notified" style={{ color: "#dc2626", fontWeight: 800 }}>✗</span>;
  return <Missing title="Notification flag not carried by the current feed" />;
}

// ── The regulation this whole area exists to satisfy ─────────────────────────
//
// WHAT THE RULE ACTUALLY SAYS, because the common paraphrase is wrong in a way
// an administrator will catch. Filing is not required OF RESIDENTS — it is
// their right. What federal law requires is of the FACILITY: a written
// grievance policy, a named Grievance Official, posted notice of how to file
// (including anonymously), a written decision to the resident, and evidence of
// the result kept for three years.
//
// Each requirement is paired with the field in this log that answers it. That
// pairing is the point: a citation on its own is decoration, and the reason
// this log has a "Notice" column at all is §483.10(j)(4)(i) — which is exactly
// the thing a surveyor asks for and the thing nobody tracks.
//
// Note on the timeframe: §483.10(j)(4)(i) requires "a reasonable expected time
// frame", set by the facility in its own policy. There is no federal number,
// so this must not imply one — the 5 days below is this log's default flag,
// and it says so.
const F585_REQUIREMENTS = [
  { cite: "§483.10(j)(1)", req: "Residents may voice grievances — orally, in writing, or anonymously — free from discrimination or reprisal.", here: "Complainant type, including anonymous" },
  { cite: "§483.10(j)(2)", req: "The facility must make prompt efforts to resolve.", here: "Days to close, flagged past the facility's own target" },
  { cite: "§483.10(j)(3)", req: "Information on how to file must be made available to the resident.", here: "Grievance policy RR-202 + posted notice" },
  { cite: "§483.10(j)(4)(i)", req: "Notice must state the right to file anonymously, the Grievance Official's contact details, a reasonable expected time frame, the right to a written decision, and how to reach the State Survey Agency, the State LTC Ombudsman and protection & advocacy.", here: "Notice column — the written decision back to the resident" },
  { cite: "§483.10(j)(4)(ii)", req: "A Grievance Official must be identified, responsible for tracking each grievance through to its conclusion.", here: "Assigned To, on every record" },
  { cite: "§483.10(j)(4)(iii)", req: "Corrective action must be taken as necessary.", here: "Resolution and outcome" },
  { cite: "§483.10(j)(4)(iv)", req: "Evidence demonstrating the result of all grievances must be kept for no less than 3 years from the decision.", here: "This log" },
];

export function F585Banner({ slaDays = 5 }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderLeft: "3px solid #0f4c81", borderRadius: 10, padding: "11px 14px", marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: "#fff", background: "#0f4c81", borderRadius: 4, padding: "2px 7px", letterSpacing: ".04em", whiteSpace: "nowrap" }}>F585</span>
        <span style={{ fontSize: 13.5, fontWeight: 800, color: "#0f172a" }}>Grievances are federally regulated</span>
        <span style={{ fontSize: 12, color: "#475569", fontFamily: "ui-monospace, Menlo, Consolas, monospace" }}>42 CFR §483.10(j)</span>
        <button type="button" onClick={() => setOpen((v) => !v)}
          style={{ marginLeft: "auto", background: "none", border: "none", color: "#0f4c81", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
          {open ? "Hide what it requires" : "What it requires →"}
        </button>
      </div>
      <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.55, marginTop: 5 }}>
        Filing is the <strong>resident’s right</strong>, not an obligation. The requirement falls on the facility:
        a written grievance policy, a named Grievance Official, a written decision back to the resident, and
        evidence of the result kept <strong>three years</strong>.
      </div>
      {open && (
        <div style={{ marginTop: 11, borderTop: "1px solid #e2e8f0", paddingTop: 10, display: "flex", flexDirection: "column", gap: 9 }}>
          {F585_REQUIREMENTS.map((r) => (
            <div key={r.cite} style={{ display: "grid", gridTemplateColumns: "minmax(110px, auto) minmax(0, 1fr)", gap: "2px 12px" }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: "#0f4c81", fontFamily: "ui-monospace, Menlo, Consolas, monospace" }}>{r.cite}</div>
              <div>
                <div style={{ fontSize: 12.5, color: "#1e293b", lineHeight: 1.5 }}>{r.req}</div>
                <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                  <span style={{ color: "#059669", fontWeight: 800 }}>→</span> {r.here}
                </div>
              </div>
            </div>
          ))}
          <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.5, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
            The rule requires “a reasonable expected time frame”, which the facility sets in its own policy —
            there is no federal number. This log flags an open grievance past <strong>{slaDays} days</strong> as
            its default; change it to match your policy.
          </div>
        </div>
      )}
    </div>
  );
}

// Empty-state panel.
export function EmptyState({ children }) {
  return (
    <Card style={{ textAlign: "center", color: "#64748b", padding: "40px 18px" }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>🗂</div>
      <div style={{ fontSize: 14 }}>{children}</div>
    </Card>
  );
}

// ── small formatters ──────────────────────────────────────────────────────────
export function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  // Render the UTC calendar date. PointClickCare dates are UTC, and date-only
  // values (YYYY-MM-DD, e.g. an ARD) parse as UTC midnight — formatting in the
  // browser's local zone would shift them a day back for any US-timezone user.
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

// State (2-letter or full name) → IANA time zone, PREDOMINANT zone for multi-zone
// states. Lets us show a facility's timestamps in its LOCAL time, derived from
// the state it's in — PCC validation req #4 (convert non-UTC to facility zone).
export const US_STATE_TIME_ZONE = {
  AL:"America/Chicago",AK:"America/Anchorage",AZ:"America/Phoenix",AR:"America/Chicago",CA:"America/Los_Angeles",
  CO:"America/Denver",CT:"America/New_York",DE:"America/New_York",DC:"America/New_York",FL:"America/New_York",
  GA:"America/New_York",HI:"Pacific/Honolulu",IA:"America/Chicago",ID:"America/Denver",IL:"America/Chicago",
  IN:"America/Indiana/Indianapolis",KS:"America/Chicago",KY:"America/New_York",LA:"America/Chicago",MA:"America/New_York",
  MD:"America/New_York",ME:"America/New_York",MI:"America/New_York",MN:"America/Chicago",MO:"America/Chicago",
  MS:"America/Chicago",MT:"America/Denver",NC:"America/New_York",ND:"America/Chicago",NE:"America/Chicago",
  NH:"America/New_York",NJ:"America/New_York",NM:"America/Denver",NV:"America/Los_Angeles",NY:"America/New_York",
  OH:"America/New_York",OK:"America/Chicago",OR:"America/Los_Angeles",PA:"America/New_York",RI:"America/New_York",
  SC:"America/New_York",SD:"America/Chicago",TN:"America/Chicago",TX:"America/Chicago",UT:"America/Denver",
  VA:"America/New_York",VT:"America/New_York",WA:"America/Los_Angeles",WI:"America/Chicago",WV:"America/New_York",
  WY:"America/Denver",PR:"America/Puerto_Rico",GU:"Pacific/Guam",VI:"America/Puerto_Rico",
};
const _STATE_NAME_TO_CODE = {
  alabama:"AL",alaska:"AK",arizona:"AZ",arkansas:"AR",california:"CA",colorado:"CO",connecticut:"CT",delaware:"DE",
  "district of columbia":"DC",florida:"FL",georgia:"GA",hawaii:"HI",idaho:"ID",illinois:"IL",indiana:"IN",iowa:"IA",
  kansas:"KS",kentucky:"KY",louisiana:"LA",maine:"ME",maryland:"MD",massachusetts:"MA",michigan:"MI",minnesota:"MN",
  mississippi:"MS",missouri:"MO",montana:"MT",nebraska:"NE",nevada:"NV","new hampshire":"NH","new jersey":"NJ",
  "new mexico":"NM","new york":"NY","north carolina":"NC","north dakota":"ND",ohio:"OH",oklahoma:"OK",oregon:"OR",
  pennsylvania:"PA","rhode island":"RI","south carolina":"SC","south dakota":"SD",tennessee:"TN",texas:"TX",utah:"UT",
  vermont:"VT",virginia:"VA",washington:"WA","west virginia":"WV",wisconsin:"WI",wyoming:"WY","puerto rico":"PR",
};

// Resolve a state (code or name) to its IANA time zone, else null.
export function stateTimeZone(state) {
  if (!state) return null;
  const s = String(state).trim();
  const code = s.length === 2 ? s.toUpperCase() : (_STATE_NAME_TO_CODE[s.toLowerCase()] || null);
  return code ? (US_STATE_TIME_ZONE[code] || null) : null;
}

// ZIP-3 prefixes in a DIFFERENT zone than their state's predominant one — the
// accurate refinement for multi-zone states (e.g. East TN is Eastern though TN
// is predominantly Central). Anything not listed uses the state default.
const ZIP3_TIME_ZONE = {
  "324":"America/Chicago","325":"America/Chicago",
  "373":"America/New_York","374":"America/New_York","376":"America/New_York","377":"America/New_York","378":"America/New_York","379":"America/New_York",
  "420":"America/Chicago","421":"America/Chicago","422":"America/Chicago","423":"America/Chicago","424":"America/Chicago",
  "463":"America/Chicago","464":"America/Chicago","476":"America/Chicago","477":"America/Chicago",
  "691":"America/Denver","693":"America/Denver","586":"America/Denver","577":"America/Denver",
  "798":"America/Denver","799":"America/Denver","885":"America/Denver","979":"America/Denver","838":"America/Los_Angeles",
};

// Pull a 5-digit ZIP off a facility (dedicated field or a free-text address).
function facilityZip(facility) {
  if (!facility) return null;
  const a = (facility.address && typeof facility.address === "object") ? facility.address : {};
  const direct = facility.zip || facility.zipCode || facility.postalCode || facility.postal_code || a.zip || a.postalCode || null;
  if (direct) { const m = String(direct).match(/(\d{5})/); if (m) return m[1]; }
  const blob = [typeof facility.address === "string" ? facility.address : null, facility.city, facility.facility_name, facility.name]
    .filter(Boolean).join(" ");
  const m = String(blob).match(/\b(\d{5})(?:-\d{4})?\b/);
  return m ? m[1] : null;
}

// A facility's IANA time zone: an explicit timeZone (the API sets it, ZIP-accurate),
// else this facility's own ZIP (accurate for multi-zone states), else the state's
// predominant zone.
export function facilityTimeZone(facility) {
  if (!facility) return null;
  if (facility.timeZone || facility.time_zone) return facility.timeZone || facility.time_zone;
  const zip = facilityZip(facility);
  const byZip = zip ? ZIP3_TIME_ZONE[zip.slice(0, 3)] : null;
  return byZip || stateTimeZone(facility.state || facility.facility_state || facility.stateCode) || null;
}

// Date + time in the facility's LOCAL zone (falls back to UTC), with the zone
// abbreviation so it's unambiguous. Pass a facility object or an IANA tz string.
export function fmtDateTime(iso, facilityOrTz) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const tz = (typeof facilityOrTz === "string" ? facilityOrTz : facilityTimeZone(facilityOrTz)) || "UTC";
  try {
    return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz, timeZoneName: "short" });
  } catch {
    return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "UTC", timeZoneName: "short" });
  }
}

export function fmtNum(n, digits = 1) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number.isInteger(n) ? String(n) : n.toFixed(digits);
}

// Highlight keyword hits in free text (case-insensitive). Returns React nodes.
export function highlight(text, query) {
  if (!text) return null;
  if (!query) return text;
  const q = query.trim();
  if (!q) return text;
  const parts = [];
  const lower = text.toLowerCase();
  const lq = q.toLowerCase();
  let i = 0;
  let key = 0;
  while (i < text.length) {
    const idx = lower.indexOf(lq, i);
    if (idx === -1) { parts.push(text.slice(i)); break; }
    if (idx > i) parts.push(text.slice(i, idx));
    parts.push(
      <mark key={key++} style={{ background: "#fef08a", color: "#713f12", padding: "0 2px", borderRadius: 3 }}>
        {text.slice(idx, idx + q.length)}
      </mark>
    );
    i = idx + q.length;
  }
  return parts;
}
